// WHAT: React component for the "Add New Workspace" modal in TimeTracQ settings
// WHY:  Provides a dedicated, reusable modal interface for users to create new workspaces
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: None
// RISKS:
//   - WARN: No internal validation of workspace name format or uniqueness.
//   - NOTE: Relies on parent for state management of modal visibility and new workspace name.

/**
 * WHAT:
 *   Props interface for the `AddWorkspaceModal` component.
 *
 * WHY:
 *   Defines the expected inputs for `AddWorkspaceModal`, ensuring type safety and clarity
 *   regarding the modal's visibility, form data, and action callbacks.
 */
interface AddWorkspaceModalProperties {
  isOpen: boolean;
  onAdd: () => Promise<void>;
  onClose: () => void;
  onWorkspaceNameChange: (name: string) => void;
  saving: boolean;
  workspaceName: string;
}

/**
 * WHAT:
 *   React component that renders a modal for adding a new TimeTracQ workspace.
 *
 * WHY:
 *   Encapsulates the UI and interaction logic for the "Add Workspace" functionality,
 *   making the main component cleaner and improving reusability.
 *   It provides an input field for the new workspace name and buttons to create or cancel.
 *
 * PROPS:
 *   - isOpen: boolean — Controls the visibility of the modal.
 *   - onClose: () => void — Callback function to close the modal.
 *   - workspaceName: string — The current value of the new workspace name input.
 *   - onWorkspaceNameChange: (name: string) => void — Callback to update the new workspace name.
 *   - onAdd: () => Promise<void> — Callback to trigger the workspace creation process.
 *   - saving: boolean — Indicates if a save operation is currently in progress, disabling the create button.
 *
 * BEHAVIOR:
 *   - Displays a modal with a title and an input field for the workspace name.
 *   - The "Create Workspace" button is disabled if the name is empty or a save operation is in progress.
 *   - Triggers `onAdd` when the "Create Workspace" button is clicked.
 *   - Triggers `onClose` when the "Cancel" button is clicked or the modal is closed externally.
 *
 * RISKS:
 *   - WARN: The modal itself does not perform validation of the workspace name (e.g., uniqueness, special characters);
 *           this is expected to be handled by the `onAdd` callback.
 *   - NOTE: The component is purely presentational and relies entirely on props for its state and behavior.
 *
 * @param root0
 *
 * @param root0.isOpen
 *
 * @param root0.onClose
 *
 * @param root0.workspaceName
 *
 * @param root0.onWorkspaceNameChange
 *
 * @param root0.onAdd
 *
 * @param root0.saving
 *
 * @returns {JSX.Element} Modal component for adding a new workspace
 *
 */
export function AddWorkspaceModal({
  isOpen,
  onAdd,
  onClose,
  onWorkspaceNameChange,
  saving,
  workspaceName,
}: Readonly<AddWorkspaceModalProperties>) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md mx-4">
        <h3 className="text-xl font-semibold text-white mb-4">Add New Workspace</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Workspace Name</label>
            <input
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => {
                onWorkspaceNameChange(e.target.value);
              }}
              placeholder="Enter workspace name..."
              type="text"
              value={workspaceName}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white px-4 py-2 rounded-lg transition-colors"
            disabled={saving || !workspaceName.trim()}
            onClick={() => {
              void onAdd();
            }}
          >
            {saving ? "Creating..." : "Create Workspace"}
          </button>
        </div>
      </div>
    </div>
  );
}
